import { useCallback, useEffect, useState } from "react";
import { Navigate, Outlet, useParams, useOutletContext } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { requestWithAuthRecovery } from "@/lib/authRequest";
import { toast } from "sonner";

const GroupLayout = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const parentContext = useOutletContext();
  const { user, initializing } = useAuth();
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [checked, setChecked] = useState(false);
  const userId = user?.id ?? null;

  const checkGroupMembership = useCallback(async () => {
    if (!groupId) {
      setIsAuthorized(true); // Allow access to show empty state
      setChecked(true);
      return;
    }

    if (!userId) {
      // ProtectedRoute owns signed-out navigation. Keep an already-authorized
      // group view mounted while the session refreshes, but never leave a new
      // mount stuck forever in the initial "Verifying access" state.
      setChecked(true);
      return;
    }

    try {
      const { data, error } = await requestWithAuthRecovery(() =>
        supabase.rpc("is_group_member", {
          _user_id: userId,
          _group_id: groupId,
        }),
      );

      if (error) {
        console.error("Error checking group membership:", error);
        const { data: directMembership, error: directError } = await requestWithAuthRecovery(() =>
          supabase
            .from("group_members")
            .select("id")
            .eq("group_id", groupId)
            .eq("user_id", userId)
            .maybeSingle(),
        );

        if (directError) {
          console.error("Error checking direct group membership:", directError);
          toast.error("Couldn't verify group access", {
            description: "Please refresh and try again.",
          });
          // Keep any previously granted access rather than bouncing the user.
          setIsAuthorized((current) => current);
          return;
        }

        if (directMembership) {
          setIsAuthorized(true);
        } else {
          setIsAuthorized((current) => (current === true ? true : false));
          toast.error("You are not authorized to view this group");
        }
      } else {
        if (data) {
          setIsAuthorized(true);
        } else {
          setIsAuthorized((current) => (current === true ? true : false));
          toast.error("You are not authorized to view this group");
        }
      }
    } catch (error) {
      console.error("Error in group authorization:", error);
      setIsAuthorized((current) => current);
      toast.error("Couldn't verify group access", {
        description: "Your workspace has been kept open. Please try again.",
      });
    } finally {
      setChecked(true);
    }
    // Re-verify only when the group or the signed-in identity changes — a
    // routine token refresh hands back a new User object with the same id and
    // must never re-run this check (a transient failure would bounce the user
    // out of their group workspace and into an empty personal sheet).
  }, [groupId, userId]);

  useEffect(() => {
    checkGroupMembership();
  }, [checkGroupMembership]);

  if (initializing || (!checked && isAuthorized === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (isAuthorized === null) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center space-y-2">
          <p className="font-medium">Group access could not be verified.</p>
          <p className="text-sm text-muted-foreground">Refresh the page to try again.</p>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return <Navigate to="/dashboard/personal/hot" replace />;
  }

  return <Outlet context={parentContext} />;
};

export default GroupLayout;
